import { Box, Typography } from "@mui/joy";
import { LogIn } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import { Switch as JoySwitch } from "@mui/joy";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../../shared";
import { useOrganizationImpersonation } from "../../../../../hooks";

type AccessSettingsProps = {
    organizationId?: string;
    isActive?: boolean;
};

export function AccessSettings({ organizationId, isActive }: AccessSettingsProps) {
    const { control } = useFormContext();
    const navigate = useNavigate();
    const { startImpersonation, isStarting } = useOrganizationImpersonation();
    const canImpersonate = !!organizationId && !!isActive;

    const handleImpersonate = async () => {
        if (!canImpersonate) return;
        try {
            await startImpersonation(organizationId);
            navigate("/");
        } catch {
            // Error snackbar is shown by useOrganizationImpersonation
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2} maxWidth={640}>
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={2}
            >
                <Box minWidth={0}>
                    <Typography level="title-md" sx={{ fontWeight: 700 }}>
                        Access & status
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                        Control whether this tenant is active and open an impersonation
                        session.
                    </Typography>
                </Box>
                {organizationId ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="soft"
                        color="primary"
                        startDecorator={<LogIn size={14} />}
                        loading={isStarting}
                        disabled={!canImpersonate}
                        onClick={handleImpersonate}
                        sx={{ borderRadius: "4px", flexShrink: 0 }}
                    >
                        Impersonate
                    </Button>
                ) : null}
            </Box>

            <Box>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>
                    Active Status
                </Typography>
                <Controller
                    name="IsActive"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <JoySwitch
                            checked={!!value}
                            onChange={(event) => onChange(event.target.checked)}
                            sx={{
                                "--Switch-thumbSize": "16px",
                                "--Switch-trackWidth": "36px",
                                "--Switch-trackHeight": "20px",
                            }}
                        />
                    )}
                />
                <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.75 }}>
                    Inactive tenants cannot be impersonated and are hidden from normal org
                    pickers.
                </Typography>
            </Box>
        </Box>
    );
}
